import React, { useState, useEffect } from 'react'
import axios from '../../../../axios-order'
import { toast } from 'react-toastify'
import ValidatedDatePicker from '../../../../components/ui/DatePicker'
import { ValidatorForm } from 'react-material-ui-form-validator'
import Checkbox from '@mui/material/Checkbox'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    IconButton,
    Typography,
    Box,
    Divider,
    Button,
    Tooltip
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

const emptyRow = () => ({
    id: Date.now() + Math.random(),
    scopeType: '',
    subScope: '',
    description: '',
    periodType: '',
    limitDigit: '',
    effectiveFromDate: null,
    effectiveToDate: null,
    periodValue: [],
})

const UploadQuotaModal = ({ open, onClose, entityId }) => {
    const [metadata, setMetadata] = useState(null)
    const [metaLoading, setMetaLoading] = useState(false)
    const [rows, setRows] = useState([emptyRow()])
    const [submitting, setSubmitting] = useState(false)
    const [logsModalOpen, setLogsModalOpen] = useState(false)
    const [logsData, setLogsData] = useState([])
    const [logsLoading, setLogsLoading] = useState(false)
    const [logsActiveTab, setLogsActiveTab] = useState('active')
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, quotaId: null })

    useEffect(() => {
        if (open) {
            setRows([emptyRow()])
            fetchMetadata()
        }
    }, [open])

    const fetchMetadata = async () => {
        setMetaLoading(true)
        try {
            const res = await axios.get('/get_quota_metadata')
            setMetadata(res.data)
        } catch (err) {
            console.error('Error fetching quota metadata:', err)
            toast.error('Failed to load quota metadata.', { autoClose: 3000 })
        } finally {
            setMetaLoading(false)
        }
    }

    const updateRow = (id, field, value) => {
        setRows(prev =>
            prev.map(row => {
                if (row.id !== id) return row
                const updated = { ...row, [field]: value }

                if (field === 'scopeType') {
                    updated.subScope = ''
                    updated.description = ''
                    updated.limitDigit = ''
                    updated.periodType = ''
                    updated.periodValue = []
                    updated.effectiveFromDate = null
                    updated.effectiveToDate = null
                    if (value === 'global' && metadata?.global_data) {
                        updated.description = metadata.global_data.join(', ')
                    }
                }
                if (field === 'subScope' && row.scopeType === 'upload_type') {
                    const found = metadata?.upload_type_data?.find(item => item.value === value)
                    updated.description = found ? found.coverage.join(', ') : ''
                    updated.periodType = ''
                    updated.periodValue = []
                }

                if (field === 'subScope' && row.scopeType === 'form_no') {
                    const found = metadata?.form_no_data?.find(f => f.form_no === value)
                    updated.description = found ? found.display_name : ''
                    updated.periodType = ''
                    updated.periodValue = []
                }

               if (field === 'periodValue') {
    updated.periodValue = Array.isArray(value) ? value : (value ? [value] : [])
}

                if (field === 'effectiveFromDate') {
                    if (updated.effectiveToDate && value && updated.effectiveToDate < value) {
                        updated.effectiveToDate = null
                    }
                }

                if (field === 'effectiveToDate') {
                    const fromDate = updated.effectiveFromDate
                    const toDate = value
                    if (
                        fromDate && toDate &&
                        fromDate !== 'Invalid Date' && toDate !== 'Invalid Date' &&
                        fromDate > toDate
                    ) {
                        toast.error('From Date cannot be greater than To Date.', { autoClose: 3000 })
                        updated[field] = null
                    }
                }

                return updated
            })
        )
    }

    const isValidDate = (v) => {
        if (!v || v === 'Invalid Date') return false
        if (!(v instanceof Date)) return false
        if (isNaN(v.getTime())) return false
        const year = v.getFullYear()
        const month = v.getMonth() + 1
        const day = v.getDate()
        if (year < 1900 || year > 2100) return false
        if (month < 1 || month > 12) return false
        if (day < 1 || day > 31) return false
        return true
    }

    const isRowComplete = row => {
        if (!row.scopeType || !row.limitDigit) return false
        if (
            (row.scopeType === 'upload_type' || row.scopeType === 'form_no') &&
            !row.subScope
        ) return false
        if (!isValidDate(row.effectiveFromDate)) return false
        if (!isValidDate(row.effectiveToDate)) return false
        return true
    }

    const canAddRow = rows.every(isRowComplete)

    const addRow = () => {
        if (!canAddRow) return
        setRows(prev => [...prev, emptyRow()])
    }

    const removeRow = id => {
        if (rows.length === 1) return
        setRows(prev => prev.filter(row => row.id !== id))
    }

    const formatDate = v => {
        if (!v || v === 'Invalid Date') return null
        const pad = '00'
        const yy = v.getFullYear().toString()
        const mm = (v.getMonth() + 1).toString()
        const dd = v.getDate().toString()
        return `${yy}-${(pad + mm).slice(-2)}-${(pad + dd).slice(-2)}`
    }

    const getSubScopeOptions = scopeType => {
        if (scopeType === 'upload_type') {
            return (metadata?.upload_type_data || []).map(item => (
                <MenuItem key={item.value} value={item.value}>{item.value}</MenuItem>
            ))
        }
        if (scopeType === 'form_no') {
            return (metadata?.form_no_data || []).map(item => (
                <MenuItem key={item.form_no} value={item.form_no}>{item.display_name}</MenuItem>
            ))
        }
        return []
    }

    const handleSubmit = async () => {
        for (const row of rows) {
            if (!row.scopeType) {
                toast.error('Please select a Scope Type for all rows.', { autoClose: 3000 })
                return
            }
            if ((row.scopeType === 'upload_type' || row.scopeType === 'form_no') && !row.subScope) {
                toast.error('Please select a Sub Scope for all applicable rows.', { autoClose: 3000 })
                return
            }
            if (!row.limitDigit) {
                toast.error('Please enter a limit for all rows.', { autoClose: 3000 })
                return
            }
            if (!row.effectiveFromDate || !isValidDate(row.effectiveFromDate) || !row.effectiveToDate || !isValidDate(row.effectiveToDate)) {
                toast.error('Please enter a valid Date.', { autoClose: 3000 })
                return
            }
            if (row.effectiveFromDate > row.effectiveToDate) {
                toast.error('From Date cannot be greater than To Date.', { autoClose: 3000 })
                return
            }
            if (row.periodType && (!row.periodValue || row.periodValue.length === 0)) {
                toast.error('Please select a Period Value for the selected Period Type.', { autoClose: 3000 })
                return
            }
        }
        const payload = {
            business_entity_id: entityId,
            quota_list: rows.map(row => ({
                scope_type: row.scopeType,
                scope_value: row.scopeType === 'global' ? null : row.subScope,
                quota_limit: parseInt(row.limitDigit, 10),
                effective_from: formatDate(row.effectiveFromDate),
                effective_to: formatDate(row.effectiveToDate),
                period_type: row.periodType || null,
                period_value: row.periodValue.length === 0 ? null : row.periodValue.length === 1 ? row.periodValue[0] : row.periodValue,
            }))
        }

        setSubmitting(true)
        try {
            await axios.post('/save_quota_configs', payload)
            toast.success('Upload quota saved successfully.', { autoClose: 3000 })
            onClose()
        } catch (err) {
            console.error('Error saving quota:', err)
            toast.error('Failed to save upload quota.', { autoClose: 3000 })
        } finally {
            setSubmitting(false)
        }
    }

    const logsTabColors = { active: '#27ae60', expired: '#c0392b', upcoming: '#e67e22' }
    const logsFilteredData = logsData.filter(q => q.quota_status === logsActiveTab)

    const LogsModal = (
        <Dialog
            open={logsModalOpen}
            onClose={() => setLogsModalOpen(false)}
            fullWidth
            maxWidth='lg'
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#17375e',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    px: 2.5
                }}
            >
                <Typography variant='subtitle1' fontWeight={600} letterSpacing={0.5}>
                    Logs
                </Typography>
                <IconButton size='small' onClick={() => setLogsModalOpen(false)} sx={{ color: '#fff' }}>
                    ✕
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2, pb: 1 }}>
                <Box display='flex' gap={1} mb={2}>
                    {['active', 'expired', 'upcoming'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setLogsActiveTab(tab)}
                            style={{
                                padding: '5px 18px',
                                borderRadius: '20px',
                                border: `1.5px solid ${logsTabColors[tab]}`,
                                backgroundColor: logsActiveTab === tab ? logsTabColors[tab] : 'transparent',
                                color: logsActiveTab === tab ? '#fff' : logsTabColors[tab],
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                            }}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </Box>

                {logsLoading ? (
                    <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress size={32} />
                    </Box>
                ) : logsFilteredData.length === 0 ? (
                    <Typography align='center' color='text.secondary' py={4}>
                        No {logsActiveTab} quotas found.
                    </Typography>
                ) : (
                    <Box display='flex' flexDirection='column' gap={1.5}>
                        {logsFilteredData.map(quota => {
                            const usedPct = quota.quota_limit > 0
                                ? Math.round((quota.used / quota.quota_limit) * 100)
                                : 0
                            const barColor = usedPct >= 90 ? '#e74c3c' : usedPct >= 60 ? '#f39c12' : '#27ae60'
                            return (
                                <Box
                                    key={quota.id}
                                    sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, backgroundColor: '#f9f9f9' }}
                                >
                                    <Box display='flex' justifyContent='space-between' mb={1}>
                                        <Box>
                                            <span style={{
                                                fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                                                color: '#17375e', backgroundColor: '#e8eef5',
                                                padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.5px'
                                            }}>
                                                {quota.scope_type}
                                            </span>
                                            {quota.scope_value && (
                                                <span style={{ marginLeft: '8px', fontSize: '13px', color: '#555' }}>
                                                    — {quota.scope_value}
                                                </span>
                                            )}
                                        </Box>
                                        <Box display='flex' flexDirection='column' alignItems='flex-end' gap={0.3}>
                                            <Typography variant='caption' color='text.secondary'>
                                                {quota.effective_from} → {quota.effective_to}
                                            </Typography>
                                            {quota.period_type && quota.period_value && (
                                                <Typography variant='caption' color='text.secondary'>
                                                    {quota.period_type.replace(/_/g, ' ').toUpperCase()} → {quota.period_value}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ height: 8, backgroundColor: '#e0e0e0', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                                        <Box sx={{ width: `${Math.min(usedPct, 100)}%`, height: '100%', backgroundColor: barColor, borderRadius: 1, transition: 'width 0.4s ease' }} />
                                    </Box>
                                    <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ fontSize: '13px' }}>
                                        <span style={{ color: '#555' }}>Used: <strong style={{ color: barColor }}>{quota.used.toLocaleString()}</strong></span>
                                        <span style={{ color: '#555' }}>Remaining: <strong style={{ color: '#27ae60' }}>{quota.remaining.toLocaleString()}</strong></span>
                                        <span style={{ color: '#555' }}>Limit: <strong style={{ color: '#17375e' }}>{quota.quota_limit.toLocaleString()}</strong></span>
                                        <span style={{ color: '#555' }}><strong style={{ color: barColor }}>{usedPct}%</strong> used</span>
                                        {(quota.quota_status === 'active' || quota.quota_status === 'upcoming') && (
                                            <IconButton
                                                size='small'
                                                sx={{ color: '#c0392b', ml: 1 }}
                                                onClick={() => setDeleteConfirm({ open: true, quotaId: quota.id })}
                                            >
                                                <DeleteOutlineIcon fontSize='small' />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 2 }}>
                <Button
                    variant='outlined'
                    size='small'
                    onClick={() => setLogsModalOpen(false)}
                    sx={{ textTransform: 'none' }}
                >
                    Close
                </Button>
            </DialogActions>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, quotaId: null })}
                maxWidth='xs'
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography fontWeight={600} fontSize='15px'>Delete Quota</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography fontSize='14px' color='text.secondary'>
                        Are you sure you want to delete this quota? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, py: 2 }}>
                    <Button
                        size='small'
                        variant='outlined'
                        onClick={() => setDeleteConfirm({ open: false, quotaId: null })}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        size='small'
                        variant='contained'
                        onClick={() => {
                            axios.delete(`/delete_quota_usage/${deleteConfirm.quotaId}`)
                                .then(() => {
                                    toast.success('Quota deleted successfully.', { autoClose: 3000 })
                                    setLogsData(prev => prev.filter(q => q.id !== deleteConfirm.quotaId))
                                    setDeleteConfirm({ open: false, quotaId: null })
                                })
                                .catch(() => toast.error('Failed to delete quota.', { autoClose: 3000 }))
                        }}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#c0392b',
                            '&:hover': { backgroundColor: '#a93226' }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    )

    const COL = {
        scopeType: { width: 150, minWidth: 140, flexShrink: 0 },
        subScope: { width: 150, minWidth: 140, flexShrink: 0 },
        description: { flex: 1, minWidth: 220 },
        periodType: { width: 140, minWidth: 130, flexShrink: 0 },
        periodValue: { width: 200, minWidth: 200, flexShrink: 0 },
        limit: { width: 100, minWidth: 90, flexShrink: 0 },
        fromDate: { width: 180, minWidth: 190, flexShrink: 0 },
        toDate: { width: 180, minWidth: 190, flexShrink: 0 },
        action: { width: 50, minWidth: 40, flexShrink: 0 }
    }

    const headerLabel = text => (
        <Typography
            variant='caption'
            fontWeight={600}
            color='text.secondary'
            sx={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
            {text}
        </Typography>
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth='xl'
            PaperProps={{ sx: { overflow: 'hidden' } }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    backgroundColor: '#17375e',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    px: 2.5
                }}
            >
                <Typography variant='subtitle1' fontWeight={600} letterSpacing={0.5}>
                    Upload Quota
                </Typography>
                <Box display='flex' alignItems='center' gap={1}>
                    <Button
                        variant='outlined'
                        size='small'
                        onClick={() => {
                            setLogsModalOpen(true)
                            setLogsLoading(true)
                            setLogsActiveTab('active')
                            axios.get(`/get_active_quotas/${entityId}`)
                                .then(res => setLogsData(res.data?.records || []))
                                .catch(() => toast.error('Failed to load logs.', { autoClose: 3000 }))
                                .finally(() => setLogsLoading(false))
                        }}
                        sx={{
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.6)',
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            py: 0.3,
                            px: 1.5,
                            '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        LOGS
                    </Button>
                    <IconButton size='small' onClick={onClose} sx={{ color: '#fff' }}>
                        ✕
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2, pb: 1, overflowX: 'auto', overflowY: 'auto' }}>
                {metaLoading ? (
                    <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
                        <Box sx={{ minWidth: 1100 }}>

                            {/* ── Header Row ── */}
                            <Box
                                display='flex'
                                alignItems='center'
                                gap={1.5}
                                sx={{ px: 1, pb: 0.5, flexWrap: 'nowrap' }}
                            >
                                <Box sx={COL.scopeType}>{headerLabel('Scope Type *')}</Box>
                                <Box sx={COL.subScope}>{headerLabel('Sub Scope')}</Box>
                                <Box sx={COL.description}>{headerLabel('Description')}</Box>
                                <Box sx={COL.periodType}>{headerLabel('Period Type')}</Box>
                                <Box sx={COL.periodValue}>{headerLabel('Period Value')}</Box>
                                <Box sx={COL.limit}>{headerLabel('Limit *')}</Box>
                                <Box sx={COL.fromDate}>{headerLabel('From Date *')}</Box>
                                <Box sx={COL.toDate}>{headerLabel('To Date *')}</Box>
                                <Box sx={COL.action} />
                            </Box>

                            <Divider sx={{ mb: 1 }} />

                            {/* ── Data Rows ── */}
                            <Box display='flex' flexDirection='column' gap={1}>
                                {rows.map((row, index) => (
                                    <Box
                                        key={row.id}
                                        display='flex'
                                        alignItems='center'
                                        gap={1.5}
                                        sx={{
                                            px: 1,
                                            py: 0.75,
                                            borderRadius: 1,
                                            flexWrap: 'nowrap',
                                            backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                                            border: '0.5px solid #e0e0e0'
                                        }}
                                    >
                                        {/* Scope Type */}
                                        <Box sx={COL.scopeType}>
                                            <FormControl fullWidth size='small' required>
                                                <InputLabel>Scope Type</InputLabel>
                                                <Select
                                                    label='Scope Type'
                                                    value={row.scopeType}
                                                    onChange={e => updateRow(row.id, 'scopeType', e.target.value)}
                                                >
                                                    <MenuItem value=''><em>Select</em></MenuItem>
                                                    {(metadata?.scope_types || []).map(type => (
                                                        <MenuItem key={type} value={type}>
                                                            {type.replace(/_/g, ' ').toUpperCase()}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        {/* Sub Scope */}
                                        <Box sx={COL.subScope}>
                                            <FormControl
                                                fullWidth
                                                size='small'
                                                disabled={row.scopeType === 'global' || !row.scopeType}
                                            >
                                                <InputLabel>Sub Scope</InputLabel>
                                                <Select
                                                    label='Sub Scope'
                                                    value={row.subScope}
                                                    onChange={e => updateRow(row.id, 'subScope', e.target.value)}
                                                >
                                                    <MenuItem value=''><em>Select</em></MenuItem>
                                                    {getSubScopeOptions(row.scopeType)}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        {/* Description */}
                                        <Box sx={COL.description}>
                                            <TextField
                                                label='Description'
                                                value={row.description}
                                                size='small'
                                                fullWidth
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        color: '#555',
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            />
                                        </Box>

                                        {/* Period Type */}
                                        <Box sx={COL.periodType}>
                                            <FormControl fullWidth size='small'>
                                                <InputLabel>Period Type</InputLabel>
                                                <Select
                                                    label='Period Type'
                                                    value={row.periodType}
                                                    onChange={e => updateRow(row.id, 'periodType', e.target.value)}
                                                >
                                                    <MenuItem value=''><em>Select</em></MenuItem>
                                                    {(metadata?.period_types || [])
                                                        .filter(pt => {
                                                            if (pt.value === 'column_wise') {
                                                                if (row.scopeType === 'upload_type') {
                                                                    const found = metadata?.upload_type_data?.find(item => item.value === row.subScope)
                                                                    return found?.is_column_wise_supported === true
                                                                }
                                                                if (row.scopeType === 'form_no') {
                                                                    const found = metadata?.form_no_data?.find(f => f.form_no === row.subScope)
                                                                    return found?.is_column_wise_supported === true
                                                                }
                                                                return false
                                                            }
                                                            return true
                                                        })
                                                        .map(pt => (
                                                            <MenuItem key={pt.value} value={pt.value}>
                                                                {pt.label}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        {/* Period Value */}
                                        <Box sx={COL.periodValue}>
                                            {(() => {
                                                const isColumnWise = row.periodType === 'column_wise'
                                                const isFinancialYear = row.periodType === 'financial_year'

                                                // const options = isFinancialYear
                                                //     ? (metadata?.value_dropdown_options?.financial_year || [])
                                                //     : isColumnWise && showColumnWise
                                                //         ? (metadata?.value_dropdown_options?.column_wise || [])
                                                //         : []

                                                // const disabled = !row.periodType || (isColumnWise && !showColumnWise)

                                                const options = isFinancialYear
                                                    ? (metadata?.value_dropdown_options?.financial_year || [])
                                                    : isColumnWise
                                                        ? (metadata?.value_dropdown_options?.column_wise || [])
                                                        : []

                                                const disabled = !row.periodType

                                                return (
                                                    <FormControl fullWidth size='small' disabled={disabled}>
                                                        <InputLabel>Period Value</InputLabel>
                                                        <Select
                                                            label='Period Value'
                                                            multiple                                         
                                                            value={row.periodValue}                          
                                                            onChange={e => updateRow(row.id, 'periodValue', e.target.value)}
                                                            renderValue={selected => Array.isArray(selected) ? selected.join(', ') : selected}   
                                                        >
                                                            {options.map(opt => (
                                                                <MenuItem key={opt.value} value={opt.value}>
                                                                    <Checkbox checked={Array.isArray(row.periodValue) && row.periodValue.includes(opt.value)} />
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )
                                            })()}
                                        </Box>

                                        {/* Limit */}
                                        <Box sx={COL.limit}>
                                            <TextField
                                                label='Limit'
                                                placeholder='0'
                                                value={row.limitDigit}
                                                onChange={e => {
                                                    if (/^\d*$/.test(e.target.value))
                                                        updateRow(row.id, 'limitDigit', e.target.value)
                                                }}
                                                size='small'
                                                fullWidth
                                                required
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                            />
                                        </Box>

                                        {/* From Date */}
                                        <Box sx={COL.fromDate}>
                                            <ValidatedDatePicker
                                                placeholder='From Date *'
                                                validators={['required']}
                                                errorMessages={['From Date is required']}
                                                value={row.effectiveFromDate}
                                                onChange={v => {
                                                    if (isValidDate(v))
                                                        updateRow(row.id, 'effectiveFromDate', v)
                                                    else
                                                        updateRow(row.id, 'effectiveFromDate', null)
                                                }}
                                            />
                                        </Box>

                                        {/* To Date */}
                                        <Box sx={COL.toDate}>
                                            <ValidatedDatePicker
                                                placeholder='To Date *'
                                                validators={['required']}
                                                errorMessages={['To Date is required']}
                                                value={row.effectiveToDate}
                                                minDateForDatepicker={row.effectiveFromDate || undefined}
                                                onChange={v => {
                                                    if (isValidDate(v))
                                                        updateRow(row.id, 'effectiveToDate', v)
                                                    else
                                                        updateRow(row.id, 'effectiveToDate', null)
                                                }}
                                            />
                                        </Box>

                                        {/* Delete */}
                                        <Box sx={COL.action}>
                                            <Tooltip title='Remove row'>
                                                <span>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => removeRow(row.id)}
                                                        disabled={false}
                                                        sx={{ color: '#c0392b' }}
                                                    >
                                                        <DeleteOutlineIcon fontSize='small' />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            {/* ── Add Row ── */}
                            <Box mt={1.5} display='flex' alignItems='center'>
                                <Tooltip title={!canAddRow ? 'Complete all fields in current rows first' : 'Add new row'}>
                                    <span>
                                        <Button
                                            variant='text'
                                            size='small'
                                            startIcon={<AddCircleOutlineIcon />}
                                            onClick={addRow}
                                            disabled={!canAddRow}
                                            sx={{
                                                textTransform: 'none',
                                                color: canAddRow ? '#17375e' : '#aaa',
                                                fontWeight: 600
                                            }}
                                        >
                                            Add Row
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                        </Box>
                    </ValidatorForm>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 2 }}>
                <Button
                    variant='outlined'
                    size='small'
                    onClick={onClose}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    size='small'
                    onClick={handleSubmit}
                    disabled={submitting || metaLoading}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#17375e',
                        '&:hover': { backgroundColor: '#122d4e' }
                    }}
                >
                    {submitting ? <CircularProgress size={18} color='inherit' /> : 'Save'}
                </Button>
            </DialogActions>
            {LogsModal}
        </Dialog>
    )
}

export default UploadQuotaModal